import { artifactUrl } from '@/lib/api/quality.js';
import type {
  CaseAttempt,
  CaseDetail,
  CaseStepDetail,
  CaseVideo,
  CaseVideoPart,
  Platform,
  ReportSession,
} from '@/lib/api/quality-types.js';

/**
 * One playable (or pending) recording surfaced in the Video tab. web → a bearer-fetched S3 clip keyed
 * by its object key; mobile → a shared BrowserStack session keyed by its session id, with the raw
 * `sessionId` exposed separately so the UI can show it muted and copyable next to the derived `label`.
 * `src` is null while the recording is still pending, never the empty string. `unavailable` marks the
 * subset of pending rows that will never gain a source — the session is closed and brought back no
 * recording — so the UI can say so instead of promising one that is coming. `caseIds` names the
 * case(s) this recording covers — always the single case that owns it for web, one or more (a
 * shared BrowserStack session, rarely) for mobile; `attemptNumber` is set only for a web recording
 * scoped to one specific attempt of a re-queued case, so a selector can bind that attempt's own
 * steps rather than the case's final ones (mobile has no attempt-scoped recording today).
 */
export interface Recording {
  key: string;
  kind: 'web' | 'mobile';
  label: string;
  src: string | null;
  pending: boolean;
  unavailable?: boolean;
  caseIds?: string[];
  attemptNumber?: number;
  sessionId?: string;
}

/** The portion of a mobile session's timeline that belongs to one case, in session-relative ms. */
export interface ClipWindow {
  startMs: number;
  endMs: number | null;
}

/**
 * One web (s3) recording a case contributes, as an explicit enumerated row rather than a fallback
 * candidate: `attempt` names the attempt it is scoped to (undefined ⇒ case-level — either the
 * case-level component artifact or the case-level scalar video, whichever this case actually has),
 * so a consumer can select "this case's own row" without guessing from a `??` chain. `collectRecordings`
 * flattens this over every case and dedupes by key; `resolveRecording` filters it down to the one row
 * a specific attempt/case asked for. A shape either both can see, or neither can.
 */
interface CaseWebRow {
  video: CaseVideo & { kind: 's3' };
  label: string;
  attemptNumber?: number;
  attempt?: CaseAttempt;
}

/**
 * Enumerates every web (s3) recording a single case contributes, across every shape the report can
 * carry it in: the case's own per-component artifacts, each attempt's per-component artifacts (falling
 * back to that attempt's own scalar video when it has none of its own — a re-queued case can mix
 * instrumentation across attempts), and finally the case-level scalar video, but only when the case
 * carries no component artifacts anywhere (a case-level scalar next to real component artifacts is
 * very likely a *different* component's clip — e.g. the mobile session that's the run's primary — and
 * mixing it in here would resurrect the exact bug (Copilot round 6, finding 1) this enumeration exists
 * to prevent: a wrong-kind value winning a fallback chain). `attemptNumber` is set (and attempt labels
 * get their "· Attempt N" suffix) only when the case actually has more than one attempt — a sole
 * attempt is never disambiguated, since there is nothing to disambiguate it from. A scalar video whose
 * case or attempt recorded several files contributes one row per file (see `scalarWebRows`).
 */
function caseWebRows(caseDetail: CaseDetail): CaseWebRow[] {
  const title = caseLabel(caseDetail);
  const attempts = caseDetail.attempts ?? [];
  const multiAttempt = attempts.length > 1;
  const hasComponentArtifacts =
    (caseDetail.videos?.length ?? 0) > 0 ||
    attempts.some((caseAttempt) => (caseAttempt.videos?.length ?? 0) > 0);
  const rows: CaseWebRow[] = [];

  for (const artifact of caseDetail.videos ?? []) {
    if (artifact.video?.kind === 's3') {
      rows.push({
        video: artifact.video,
        label: `${title} · ${artifact.component}`,
      });
    }
  }

  for (const caseAttempt of attempts) {
    const componentRows = (caseAttempt.videos ?? [])
      .filter((artifact) => artifact.video?.kind === 's3')
      .map((artifact) => ({
        video: artifact.video as CaseVideo & { kind: 's3' },
        label: multiAttempt
          ? `${title} · ${artifact.component} · Attempt ${caseAttempt.attempt + 1}`
          : `${title} · ${artifact.component}`,
        attemptNumber: multiAttempt ? caseAttempt.attempt : undefined,
        attempt: caseAttempt,
      }));
    if (componentRows.length > 0) {
      rows.push(...componentRows);
    } else if (caseAttempt.video?.kind === 's3') {
      rows.push(
        ...scalarWebRows(caseAttempt.video, caseAttempt.video_parts, {
          label: multiAttempt
            ? `${title} · Attempt ${caseAttempt.attempt + 1}`
            : title,
          attemptNumber: multiAttempt ? caseAttempt.attempt : undefined,
          attempt: caseAttempt,
        }),
      );
    }
  }

  if (!hasComponentArtifacts && caseDetail.video?.kind === 's3') {
    rows.push(
      ...scalarWebRows(caseDetail.video, caseDetail.video_parts, {
        label: title,
      }),
    );
  }

  return rows;
}

/**
 * The rows one scalar web video contributes: the video itself, or every file in `parts`, oldest
 * first, when the case recorded more than one. Playwright writes a file per page, so a second tab or
 * a completion recovery's fresh browser context adds one, and each file gets its own row so none
 * hides behind the first.
 */
function scalarWebRows(
  video: CaseVideo & { kind: 's3' },
  parts: CaseVideoPart[] | undefined,
  row: Omit<CaseWebRow, 'video'>,
): CaseWebRow[] {
  if (!parts || parts.length < 2) return [{ ...row, video }];
  return parts.map((part, index) => ({
    ...row,
    video: part,
    label: `${row.label} · Recording ${index + 1} of ${parts.length}`,
  }));
}

/**
 * Every recording of a run: one entry per web S3 key (deduped across every shape `caseWebRows`
 * enumerates for a case) plus one entry per BrowserStack session — playable, still recording, or
 * closed with nothing to play.
 */
export function collectRecordings(input: {
  runId: string;
  cases?: CaseDetail[];
  sessions?: ReportSession[];
  /** Whether the run itself has reached a terminal state. A session's own `pending` only says the
   *  recording was unresolvable when the report was written; on a cancelled or finished run nothing
   *  will resolve it later, so a still-pending session there is unavailable, not still recording. */
  runIsTerminal?: boolean;
}): Recording[] {
  const webRecordings = new Map<string, Recording>();

  for (const caseDetail of input.cases ?? []) {
    for (const row of caseWebRows(caseDetail)) {
      /** Whichever row reaches a given key first wins — a case-level row and an attempt's row
       *  commonly share the same key (the case's final attempt), so this is normally a no-op, not a
       *  real conflict. */
      if (webRecordings.has(row.video.key)) continue;
      const pending = row.video.status === 'pending';
      webRecordings.set(row.video.key, {
        key: row.video.key,
        kind: 'web',
        label: row.label,
        src: pending ? null : artifactUrl(input.runId, row.video.key),
        pending,
        caseIds: [caseDetail.id],
        attemptNumber: row.attemptNumber,
      });
    }
  }

  /** Every session the report carries gets a row, including one that closed without a recording.
   *  Dropping those left the Video tab empty — and disabled — with nothing to explain why, which is
   *  how a whole client's mobile runs looked while the worker was blanking their video URLs. */
  const mobileRecordings = (input.sessions ?? []).map(
    (reportSession): Recording => {
      const sessionOpen = reportSession.status === 'pending';
      const hasSource = Boolean(reportSession.video?.value);
      /** `pending` keeps its existing meaning exactly — no source, or a session the report left
       *  open — because it is what suppresses metadata, loading and seeking in `PlayerDock`. The
       *  run's terminal state only decides whether a source-less row is still coming or never was. */
      const unavailable =
        !hasSource && (!sessionOpen || input.runIsTerminal === true);
      return {
        key: reportSession.session_id,
        kind: 'mobile',
        label: mobileSessionLabel(reportSession, input.cases ?? []),
        src: hasSource ? (reportSession.video?.value ?? null) : null,
        pending: !hasSource || sessionOpen,
        ...(unavailable ? { unavailable: true } : {}),
        caseIds: reportSession.case_ids,
        sessionId: reportSession.session_id,
      };
    },
  );

  return [...webRecordings.values(), ...mobileRecordings];
}

/**
 * The cases from `caseIds` that this report actually carries — an id can name a case the report
 * doesn't have (AC 17, an unattributed BrowserStack session whose `case_ids` outlived the cases they
 * pointed at), and no consumer may treat that as real. Shared by every reader of a session's case
 * list (`mobileSessionLabel`, `wholeRecordingSelection`, `VideoReport`'s pill list) so an unknown id
 * never reaches a `caseId` that scopes the dock, the URL, or a selectable row.
 */
export function knownSessionCases(
  caseIds: string[],
  cases: CaseDetail[] | undefined,
): CaseDetail[] {
  return caseIds
    .map((caseId) => cases?.find((caseDetail) => caseDetail.id === caseId))
    .filter((caseDetail): caseDetail is CaseDetail => caseDetail !== undefined);
}

/**
 * Human label for a BrowserStack session: the one case it covers when exactly one is known from this
 * report, a first-to-last span when it covers several, or the raw session id when none of its cases
 * made it into this report (an unattributed session still gets a recording, just an anonymous one).
 *
 * The span form carries case NUMBERS only (`#188461 → #188462 · 2 cases`) — two full
 * number-plus-title labels joined by an arrow overflow any row they land in, and the per-case
 * sub-rows underneath already spell out each case in full.
 */
export function mobileSessionLabel(
  session: ReportSession,
  cases: CaseDetail[],
): string {
  const knownCases = knownSessionCases(session.case_ids, cases);

  const [firstCase] = knownCases;
  if (!firstCase) return `Mobile session ${session.session_id}`;
  if (knownCases.length === 1) return caseLabel(firstCase);

  const lastCase = knownCases[knownCases.length - 1] ?? firstCase;
  return `#${firstCase.id} → #${lastCase.id} · ${knownCases.length} cases`;
}

/**
 * A case's display name for every player surface: the case NUMBER first, then the title
 * (`#188455 · Verification of "Visit" creation`). The number is the key people cross-reference with
 * the Cases table, TestRail and Jira, and it is the only part that disambiguates two cases sharing a
 * title — a real run carries "Verification of 'Visit' creation" twice. A case the report gives no
 * title for renders the number alone rather than repeating it twice.
 */
export function caseLabel(caseDetail: CaseDetail): string {
  return caseDetail.title
    ? `#${caseDetail.id} · ${caseDetail.title}`
    : `#${caseDetail.id}`;
}

/**
 * The case-relative clip window carried by a mobile session recording, or null for a web recording,
 * an absent recording, or a session recording whose window hasn't been computed yet — that state is
 * live on dev (a session can flip 'ready' before its per-case clip bounds land), and callers must fall
 * back to the full session timeline rather than treat it as an error.
 */
export function clipWindow(
  video: CaseVideo | null | undefined,
): ClipWindow | null {
  if (!video || video.kind !== 'session') return null;
  if (video.clip_start_ms == null) return null;
  return { startMs: video.clip_start_ms, endMs: video.clip_end_ms ?? null };
}

/**
 * Clamps a timecode to a clip window: unchanged inside it (boundaries inclusive), null outside it,
 * unchanged when there is no window (passthrough), and null for a null/undefined timecode — a step
 * past the end of a recording carries none, and seeking to it would be wrong rather than merely
 * imprecise.
 */
export function boundToClip(
  ms: number | null | undefined,
  window: ClipWindow | null,
): number | null {
  if (ms == null) return null;
  if (!window) return ms;
  if (ms < window.startMs) return null;
  if (window.endMs != null && ms > window.endMs) return null;
  return ms;
}

/**
 * Where a specific case's clip begins inside a shared BrowserStack session, for the Video tab's
 * per-case selector (AC 16): the case's own `clip_start_ms` when its scalar or per-component video
 * names this session, else null — a session with no window for this case (Branch B) or a case not
 * carried in this report (AC 17, unattributed) seeks nowhere, and the caller starts the session from
 * the top instead.
 */
export function caseClipStartMs(
  caseDetail: CaseDetail | undefined,
  sessionId: string,
): number | null {
  if (!caseDetail) return null;
  const video =
    caseDetail.video?.kind === 'session' &&
    caseDetail.video.session_id === sessionId
      ? caseDetail.video
      : caseDetail.videos?.find(
          (artifact) =>
            artifact.video?.kind === 'session' &&
            artifact.video.session_id === sessionId,
        )?.video;
  return clipWindow(video)?.startMs ?? null;
}

/**
 * Where selecting a RECORDING as a whole (not one of its covered cases) should land: the seek target
 * (a single-case mobile session's clip start, else null for a web recording, a multi-case session, or
 * a session covering none) and the case to scope chapters to (that same single case, but only when it
 * is actually carried in this report — AC 17, unattributed, leaves this undefined rather than
 * inventing one). "Single-case" is decided on the KNOWN case count, not the raw `caseIds` length — a
 * session naming one real case plus other ids this report doesn't carry is still single-case (finding
 * 4); only the actually-known cases can ever scope chapters or a seek anyway. Shared by the Video
 * tab's own row selector (`VideoReport`) and the "Show video" control (`QualityRunDetail`'s
 * `onShowVideo`), so a single-case mobile recording lands at the same moment from either entry point.
 */
export function wholeRecordingSelection(
  recording: Recording,
  cases?: CaseDetail[],
): { seekMs: number | null; caseId?: string } {
  const knownCases = knownSessionCases(recording.caseIds ?? [], cases);
  const [soleKnownCase] = knownCases.length === 1 ? knownCases : [];
  const caseId = soleKnownCase?.id;
  const seekMs =
    recording.kind === 'mobile' && soleKnownCase
      ? caseClipStartMs(soleKnownCase, recording.key)
      : null;
  return { seekMs, caseId };
}

/**
 * The bound case's own window inside a shared BrowserStack session — used to scope the chapters
 * list (S9) to the moment the docked recording actually covers for this case, never the whole
 * session. Checks, in order: the specific attempt's video (an earlier attempt's own recording, for
 * a re-queued case), the attempt's per-component videos, then the case's scalar and per-component
 * videos. Null when nothing matches this session id, or when the match carries no window yet
 * (Branch B — the caller falls back to the full session timeline).
 */
export function caseClipWindow(
  caseDetail: CaseDetail | undefined,
  attempt: CaseAttempt | undefined,
  sessionId: string,
): ClipWindow | null {
  const candidates: (CaseVideo | null | undefined)[] = [
    attempt?.video,
    ...(attempt?.videos ?? []).map((artifact) => artifact.video),
    caseDetail?.video,
    ...(caseDetail?.videos ?? []).map((artifact) => artifact.video),
  ];
  const video = candidates.find(
    (candidate) =>
      candidate?.kind === 'session' && candidate.session_id === sessionId,
  );
  return clipWindow(video);
}

/**
 * The one seam between "which case/attempt/recording the dock is bound to" and "what the chapters
 * list (S9) should render" — both `handleSeek` (Cases tab) and `handleVideoSelect` (Video tab) funnel
 * through this after binding, so a selection from either surface scopes chapters the same way.
 * Attempt-scoped steps when `boundAttemptNumber` names one (a re-queued case's own recording), else
 * the case's own steps; the clip window only applies to a mobile recording — a web recording has no
 * shared timeline to bound against. When the bound file is one of several (`video_parts` of the bound
 * attempt, or of the case or any attempt when none is bound), only the steps of that file are kept,
 * since each file has its own timeline. A step names its file in
 * `video_key`; a step that names none belongs to the earliest file, which is where its jump lands.
 */
export function resolveChapterScope(input: {
  cases?: CaseDetail[];
  boundCaseId: string | null;
  boundAttemptNumber: number | null;
  boundRecording: Recording | null;
}): { steps: CaseStepDetail[]; clipWindow: ClipWindow | null } {
  const caseDetail = input.cases?.find(
    (candidate) => candidate.id === input.boundCaseId,
  );
  const attempt =
    input.boundAttemptNumber !== null
      ? caseDetail?.attempts?.find(
          (candidate) => candidate.attempt === input.boundAttemptNumber,
        )
      : undefined;
  const ownSteps = attempt?.steps ?? caseDetail?.steps ?? [];
  const boundWebKey =
    input.boundRecording?.kind === 'web' ? input.boundRecording.key : null;
  const candidatePartLists = attempt
    ? [attempt.video_parts]
    : [
        caseDetail?.video_parts,
        ...(caseDetail?.attempts ?? []).map(
          (caseAttempt) => caseAttempt.video_parts,
        ),
      ];
  const parts =
    candidatePartLists.find((partList) =>
      partList?.some((part) => part.key === boundWebKey),
    ) ?? [];
  const steps =
    boundWebKey && parts.length > 1
      ? ownSteps.filter(
          (step) => (step.video_key ?? parts[0]!.key) === boundWebKey,
        )
      : ownSteps;
  const clipWindowForChapters =
    input.boundRecording?.kind === 'mobile'
      ? caseClipWindow(caseDetail, attempt, input.boundRecording.key)
      : null;
  return { steps, clipWindow: clipWindowForChapters };
}

/**
 * Jumps the player to a step's moment, shared by every surface that offers one (the steps table and
 * the time-sink strip). `component` targets that component's recording, `attempt` a re-queued case's
 * own attempt, and `videoKey` the file the step ran in when the case recorded several (the step's
 * `video_key`).
 */
export type StepSeekHandler = (
  caseId: string,
  ms: number,
  component?: string,
  attempt?: CaseAttempt,
  videoKey?: string,
) => void;

/**
 * Resolves the recording a step's "jump to video" should seek: the component the step ran on
 * ('web'/'mobile'), falling back to the run's platform when the step predates per-step component
 * attribution, and, for a re-queued case, the specific attempt it belongs to (so an earlier attempt's
 * step seeks its OWN recording rather than the case-level one). Returns the pending recording rather
 * than null when the target recording exists but isn't ready yet, so the caller can show the pending
 * frame instead of refusing the seek.
 *
 * The web path filters `caseWebRows` down to the one row the request named, rather than chaining
 * candidates with `??` — a chain silently prefers whichever candidate is truthy first regardless of
 * its kind, which is exactly how a case-level mobile-session scalar used to shadow a real web clip
 * living on the case's sole attempt (Copilot round 6, finding 1). Explicit filtering by attempt
 * cannot make that mistake: every row `caseWebRows` produces is already a web (s3) clip.
 *
 * `videoKey` is the step's own `video_key`: when the case recorded several files it picks the file the
 * step's timecode is measured in, among the named attempt's rows or, with no attempt named, among all
 * of the case's rows. Without it, the earliest file of the rows the request named.
 */
export function resolveRecording(input: {
  recordings: Recording[];
  cases: CaseDetail[];
  caseId: string;
  component?: string;
  attempt?: CaseAttempt;
  platform?: Platform;
  videoKey?: string;
}): Recording | null {
  const mode =
    input.component === 'web'
      ? 'web'
      : input.component === 'mobile'
        ? 'mobile'
        : input.platform;

  if (mode === 'mobile') {
    const attemptSessionId =
      input.attempt?.video?.kind === 'session'
        ? input.attempt.video.session_id
        : input.attempt?.videos
            ?.map((artifact) => artifact.video)
            .find((video) => video?.kind === 'session')?.session_id;

    const recording = attemptSessionId
      ? input.recordings.find(
          (candidate) =>
            candidate.kind === 'mobile' && candidate.key === attemptSessionId,
        )
      : input.recordings.find(
          (candidate) =>
            candidate.kind === 'mobile' &&
            candidate.caseIds?.includes(input.caseId),
        );
    return recording ?? null;
  }

  const caseDetail = input.cases.find(
    (candidate) => candidate.id === input.caseId,
  );
  if (!caseDetail) return null;
  const rows = caseWebRows(caseDetail);

  /** A named attempt (a re-queued case's OWN step) binds to exactly that attempt's rows. With no
   *  attempt named, the case-level rows win when the case has any (its own component artifact or its
   *  scalar video); failing that, a case with exactly one attempt falls back to that sole attempt's
   *  rows — unambiguous, since there is nothing else it could mean. */
  const caseLevelRows = rows.filter(
    (candidate) => candidate.attempt === undefined,
  );
  const scopedRows = input.attempt
    ? rows.filter(
        (candidate) => candidate.attempt?.attempt === input.attempt!.attempt,
      )
    : caseLevelRows.length > 0
      ? caseLevelRows
      : caseDetail.attempts?.length === 1
        ? rows.filter(
            (candidate) => candidate.attempt === caseDetail.attempts![0],
          )
        : [];
  const keyedRow = input.videoKey
    ? (input.attempt ? scopedRows : rows).find(
        (candidate) => candidate.video.key === input.videoKey,
      )
    : undefined;
  const row = keyedRow ?? scopedRows[0];

  if (!row) return null;
  return (
    input.recordings.find(
      (candidate) =>
        candidate.kind === 'web' && candidate.key === row.video.key,
    ) ?? null
  );
}

/**
 * Resolves a deep link's target recording (S11, AC 26/27): prefers the URL's `rec` key when it
 * still names a recording in this run — a link revisited after the report changed can have one
 * drop out — falling back to resolving it from `case` the same way a step's timecode click does
 * (`resolveRecording`). Returns null when neither names anything reachable, so the caller leaves
 * the dock untouched rather than binding the wrong recording.
 */
export function resolveDeepLinkRecording(input: {
  recordings: Recording[];
  cases: CaseDetail[];
  caseId?: string;
  recordingKey?: string;
  platform?: Platform;
}): Recording | null {
  if (input.recordingKey) {
    const named = input.recordings.find(
      (candidate) => candidate.key === input.recordingKey,
    );
    if (named) return named;
  }
  if (!input.caseId) return null;
  return resolveRecording({
    recordings: input.recordings,
    cases: input.cases,
    caseId: input.caseId,
    platform: input.platform,
  });
}
